import { PlusOutlined } from '@ant-design/icons';
import { Button, Form, GetProp, Image, Select, Upload } from 'antd';
import { UploadFile, UploadProps } from 'antd/es/upload';
import { getApi, postApi, putApi } from 'apis';
import AutoCompleteField from 'components/Global/AutoCompleteField';
import InputWithLabelAndSvg from 'components/Global/InputWithLabel';
import { FORMDATA, sanitizeString } from 'constants/Data';
import { getNames } from 'country-list';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { checkPermission, toastText } from 'utils/utils';
import styles from '../../Medicines/AddMedicineBody/index.module.scss';
import '../../Medicines/AddMedicineBody/index.scss';
import {
	buildGroceryClipboardPayload,
	loadGroceryClipboardPayload,
	normalizeClipboardFieldsForPaste,
	saveGroceryClipboardPayload,
} from '../groceryFormClipboard';

function cloneClipboardFields(fields: Record<string, unknown>): Record<string, unknown> {
	try {
		if (typeof structuredClone === 'function') {
			return structuredClone(fields);
		}
	} catch {
		// structuredClone can throw on non-cloneable values; fall back below
	}
	return JSON.parse(JSON.stringify(fields)) as Record<string, unknown>;
}

function isEditableEventTarget(target: EventTarget | null): boolean {
	const element = target as HTMLElement | null;
	if (!element) return false;
	const editableSelector =
		'input, textarea, [contenteditable=""], [contenteditable="true"], [role="textbox"], [role="combobox"], .ant-select, .ant-select-selector, .ant-select-selection-search-input';
	return Boolean(element.closest(editableSelector));
}

type Category = {
	id: string;
	name: string;
	subCategories: { id: string; name: string; categoryId: string }[];
};

const resolveImageSrc = (key: string) => {
	if (!key) return '';
	if (key.startsWith('http://') || key.startsWith('https://')) return key;
	return `${process.env.REACT_APP_S3_BASE_URL}${key}`;
};

const defaultAddValues = {
	isPerishable: 'false',
	isPackaged: 'false',
	isHalalCertified: 'false',
};

const GROCERY_SUGGESTION_FIELDS = [
	'productName',
	'brand',
	'manufacturerName',
	'marketedBy',
	'storageInstructions',
	'fssaiLicenseNo',
	'hsnCode',
	'productCode',
	'shelfLife',
	'numberOfItems',
	'ingredients',
] as const;

const AddEditGroceryBody: FC<any> = (props) => {
	const { addGroceryFields } = FORMDATA;
	const {
		closeDrawerByAnimation,
		editSelectedGrocery,
		fetchGroceries,
		setEditSelectedGrocery,
		optionsState,
		setOptionsState,
	} = props;

	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [categoryList, setCategoryList] = useState<{ label: string; value: string }[]>([]);
	const [wholeCategories, setWholeCategories] = useState<Category[]>([]);
	const [selectedCategoryId, setSelectedCategoryId] = useState('');
	const [fileList, setFileList] = useState<UploadFile[]>([]);
	const [primaryFileList, setPrimaryFileList] = useState<UploadFile[]>([]);
	const [previewOpen, setPreviewOpen] = useState(false);
	const [previewImage, setPreviewImage] = useState('');

	const userProfile = useSelector((state: any) => state?.userProfile);
	type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];
	const isEditMode = Boolean(editSelectedGrocery?.id);

	const isGroceryAddPermission = checkPermission(userProfile?.data?.permissions, {
		permissionName: 'GroceryItems',
		permission: ['add'],
	});
	const isGroceryEditPermission = checkPermission(userProfile?.data?.permissions, {
		permissionName: 'GroceryItems',
		permission: ['edit'],
	});
	const isImageAddPermission = checkPermission(userProfile?.data?.permissions, {
		permissionName: 'Images',
		permission: ['add'],
	});
	const isImageEditPermission = checkPermission(userProfile?.data?.permissions, {
		permissionName: 'Images',
		permission: ['edit'],
	});
	const canViewImages = checkPermission(userProfile?.data?.permissions, {
		permissionName: 'Images',
		permission: ['view'],
	});
	const canManageImages = isImageEditPermission || isImageAddPermission;
	const shouldDisableNonImageFields = isEditMode
		? !isGroceryEditPermission && isImageEditPermission
		: !isGroceryAddPermission;
	const canSubmit = isEditMode
		? isGroceryEditPermission || isImageEditPermission
		: isGroceryAddPermission;

	const countries = getNames().map((name) => {
		if (name === 'Korea, Republic of') {
			return { label: 'South Korea', value: 'South Korea' };
		}
		if (name === "Korea, Democratic People's Republic of") {
			return { label: 'North Korea', value: 'North Korea' };
		}
		return { label: name, value: name };
	});

	const subCategoryOptions = useMemo(() => {
		return (
			wholeCategories
				.find((item) => item.id === selectedCategoryId)
				?.subCategories?.map((sub) => ({
					label: sub.name,
					value: sub.id,
				})) || []
		);
	}, [wholeCategories, selectedCategoryId]);

	const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const isApplyingClipboardRef = useRef(false);
	const debounceDelay = 400;

	const toOptions = (values: unknown[]): { label: string; value: string }[] => {
		const unique = new Set<string>();
		for (const v of values) {
			if (typeof v === 'string' && v) unique.add(v);
		}
		return Array.from(unique).map((v) => ({ label: v, value: v }));
	};

	const handleSearch = useCallback(
		async (value: string, field: keyof typeof optionsState) => {
			const stripTags = (str: string) => str?.replace(/<\/?[^>]+(>|$)/g, '');
			const searchValue = stripTags(value?.trim() || '');
			const sanitizedValue = stripTags(value?.trim() || '');

			if (
				!GROCERY_SUGGESTION_FIELDS.includes(
					field as (typeof GROCERY_SUGGESTION_FIELDS)[number],
				) ||
				!searchValue ||
				!sanitizedValue ||
				sanitizedValue.length < 3
			) {
				setOptionsState((prev: typeof optionsState) => ({ ...prev, [field]: [] }));
				return;
			}

			if (debounceTimeoutRef.current) {
				clearTimeout(debounceTimeoutRef.current);
			}

			debounceTimeoutRef.current = setTimeout(async () => {
				if (!sanitizedValue) {
					setOptionsState((prev: typeof optionsState) => ({ ...prev, [field]: [] }));
					return;
				}

				try {
					const query = {
						targetField: field,
						search: searchValue,
					};
					const res = await getApi('/grocery/get-all', query);
					const fieldValues = res?.data?.data?.map((item: any) => item[field]);
					const formattedOptions = toOptions(fieldValues as unknown[]);
					setOptionsState((prev: typeof optionsState) => ({
						...prev,
						[field]: formattedOptions,
					}));
				} catch {
					setOptionsState((prev: typeof optionsState) => ({ ...prev, [field]: [] }));
				}
			}, debounceDelay);
		},
		[setOptionsState],
	);

	const fetchCategories = async () => {
		try {
			const res = await getApi('/grocery-category/get-all');
			if (res?.data?.responseStatus !== 200 && !res?.data?.data) {
				throw new Error(res?.data?.message || 'Failed to fetch categories.');
			}
			const categories = res.data.data || [];
			setWholeCategories(categories);
			setCategoryList(
				categories.map((item: Category) => ({
					label: item.name,
					value: item.id,
				})),
			);
		} catch (error: any) {
			toastText(error?.response?.data?.message || error.message, 'error');
		}
	};

	useEffect(() => {
		fetchCategories();
	}, []);

	useEffect(() => {
		if (!isEditMode) {
			form.resetFields();
			form.setFieldsValue(defaultAddValues);
			setSelectedCategoryId('');
			setFileList([]);
			setPrimaryFileList([]);
			setPreviewOpen(false);
			setPreviewImage('');
			return;
		}

		const item = editSelectedGrocery;
		setSelectedCategoryId(item.categoryId || '');
		form.setFieldsValue({
			...item,
			mrp: item?.mrp?.toString(),
			taxRate: item?.taxRate?.toString(),
			weightage: item?.weightage?.toString(),
			retailPriceInr: item?.retailPriceInr?.toString(),
			isPerishable: item?.isPerishable ? 'true' : 'false',
			isPackaged: item?.isPackaged ? 'true' : 'false',
			isHalalCertified: item?.isHalalCertified ? 'true' : 'false',
		});

		const secondary = (item?.productSecondaryImageUrl || []).map(
			(url: string, index: number) => ({
				uid: index.toString(),
				name: url,
				status: 'done' as const,
				url: resolveImageSrc(url),
			}),
		);
		setFileList(secondary);
		setPrimaryFileList(
			item?.productPrimaryImageUrl
				? [
						{
							uid: '1',
							name: item.productPrimaryImageUrl,
							status: 'done' as const,
							url: resolveImageSrc(item.productPrimaryImageUrl),
						},
					]
				: [],
		);
	}, [editSelectedGrocery, isEditMode, form]);

	const getDropdownOptions = (fieldId: string, roleOptions?: { label: string; value: string }[]) => {
		switch (fieldId) {
			case 'countryOfOrigin':
				return countries;
			case 'categoryId':
				return categoryList;
			case 'subCategoryId':
				return subCategoryOptions;
			default:
				return roleOptions || [];
		}
	};

	const onFinish = async (values: any) => {
		if (loading) return;
		if (isEditMode && !editSelectedGrocery?.id) return;
		if (!isEditMode && !isGroceryAddPermission) return;

		const { primaryImage, secondaryImage, ...restData } = values;
		setLoading(true);
		const formData = new FormData();

		if (canManageImages) {
			let primaryImageUrl: File | null = null;
			let existingPrimaryImageUrl: string | null = null;

			if (primaryFileList?.length) {
				const file = primaryFileList[0];
				if ('originFileObj' in file && file.originFileObj instanceof File) {
					primaryImageUrl = file.originFileObj;
				} else if (file.url) {
					existingPrimaryImageUrl = file.name;
				}
			}

			if (primaryImageUrl) {
				formData.append('productPrimaryImageUrl', primaryImageUrl);
			} else if (isEditMode && existingPrimaryImageUrl) {
				formData.append('existingProductPrimaryImageUrl', existingPrimaryImageUrl);
			}

			const existingSecondaryImageUrls: string[] = [];
			const newFiles: File[] = [];

			fileList?.forEach((file) => {
				if ('originFileObj' in file && file.originFileObj instanceof File) {
					newFiles.push(file.originFileObj);
				} else if (file.url) {
					existingSecondaryImageUrls.push(file.name);
				}
			});

			newFiles.forEach((file) => {
				formData.append('productSecondaryImageUrl', file);
			});

			if (isEditMode) {
				formData.append(
					'existingProductSecondaryImageUrls',
					JSON.stringify(existingSecondaryImageUrls),
				);
			}
		} else if (isEditMode && editSelectedGrocery) {
			if (editSelectedGrocery.productPrimaryImageUrl) {
				formData.append(
					'existingProductPrimaryImageUrl',
					editSelectedGrocery.productPrimaryImageUrl,
				);
			}
			if (editSelectedGrocery.productSecondaryImageUrl?.length) {
				formData.append(
					'existingProductSecondaryImageUrls',
					JSON.stringify(editSelectedGrocery.productSecondaryImageUrl),
				);
			}
		}

		Object.entries(restData).forEach(([key, value]) => {
			if (value === undefined || value === null) return;
			if (
				typeof value === 'object' &&
				value !== null &&
				'value' in value &&
				'label' in value
			) {
				formData.append(key, sanitizeString((value as { value: string }).value));
			} else if (typeof value === 'string') {
				formData.append(key, sanitizeString(value));
			} else {
				formData.append(key, String(value));
			}
		});

		try {
			const res = isEditMode
				? await putApi(`/grocery/update/${editSelectedGrocery.id}`, formData, true)
				: await postApi('/grocery/create', formData, true);

			if (res?.data?.responseStatus === 200) {
				toast.success(res?.data?.message);
				fetchGroceries();
				closeDrawerByAnimation();
			} else {
				throw new Error(res?.data?.message || 'Something went wrong.');
			}
		} catch (error: any) {
			toast.error(
				error?.response?.data?.message ||
					error?.response?.data?.error?.errorDescription ||
					error.message ||
					'Something went wrong.',
			);
		} finally {
			setLoading(false);
		}
	};

	const handleOnClick = () => {
		closeDrawerByAnimation();
		setEditSelectedGrocery?.(null);
	};

	const handleCopyGroceryFields = useCallback(() => {
		if (!isEditMode) return;
		const values = form.getFieldsValue(true) as Record<string, unknown>;
		const payload = buildGroceryClipboardPayload(values);
		saveGroceryClipboardPayload(payload);
		toast.success('Grocery fields copied');
	}, [form, isEditMode]);

	const handlePasteGroceryFields = useCallback(async () => {
		if (isEditMode) return;
		const payload = loadGroceryClipboardPayload();
		if (!payload) {
			toast.error('No copied grocery data. Use Copy while editing a grocery first.');
			return;
		}
		const fields = normalizeClipboardFieldsForPaste(
			cloneClipboardFields(payload.fields as Record<string, unknown>),
		);
		const catId = typeof fields.categoryId === 'string' ? fields.categoryId : '';
		flushSync(() => {
			setSelectedCategoryId(catId);
		});
		isApplyingClipboardRef.current = true;
		form.setFieldsValue(fields);
		setTimeout(() => {
			isApplyingClipboardRef.current = false;
		}, 0);
		setPrimaryFileList([]);
		setFileList([]);
		setPreviewImage('');
		setPreviewOpen(false);
		toast.success('Pasted grocery fields');
	}, [form, isEditMode]);

	useEffect(() => {
		const onKeyDown = (event: KeyboardEvent) => {
			if (isEditableEventTarget(event.target)) return;
			if (!(event.ctrlKey && event.altKey)) return;
			const key = event.key.toLowerCase();

			if (key === 'v') {
				if (isEditMode || loading || shouldDisableNonImageFields || !isGroceryAddPermission) return;
				event.preventDefault();
				void handlePasteGroceryFields();
				return;
			}

			if (key === 'c') {
				const canUseCopyShortcut =
					isEditMode && (isGroceryEditPermission || isImageEditPermission) && !loading;
				if (!canUseCopyShortcut) return;
				event.preventDefault();
				handleCopyGroceryFields();
			}
		};

		window.addEventListener('keydown', onKeyDown);
		return () => {
			window.removeEventListener('keydown', onKeyDown);
		};
	}, [
		handleCopyGroceryFields,
		handlePasteGroceryFields,
		isEditMode,
		isGroceryAddPermission,
		isGroceryEditPermission,
		isImageEditPermission,
		loading,
		shouldDisableNonImageFields,
	]);

	const getBase64 = (file: FileType): Promise<string> =>
		new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => resolve(reader.result as string);
			reader.onerror = (error) => reject(error);
		});

	const handlePreview = async (file: UploadFile) => {
		if (!canViewImages) {
			return;
		}

		if (!file.url && !file.preview) {
			file.preview = await getBase64(file.originFileObj as FileType);
		}

		setPreviewImage(file.url || (file.preview as string));
		setPreviewOpen(true);
	};

	const handleChange = (
		{ file }: { file: UploadFile; fileList: UploadFile[] },
		_type: 'primary' | 'secondary',
	) => {
		if (file?.size) {
			const isLt1M = file.size / 1024 / 1024 < 1;
			if (!isLt1M) {
				toast.error('Image must be smaller than 1MB!');
			}
		}
	};

	const uploadButton = (
		<button style={{ border: 0, background: 'none' }} type="button">
			<PlusOutlined />
			<div style={{ marginTop: 8 }}>Upload</div>
		</button>
	);

	const propsUpload = {
		name: 'file',
		accept: '.jpg,.jpeg,.png,.webp',
		maxCount: 3,
		multiple: true,
		fileList,
		beforeUpload: () => false,
		listType: 'picture-card' as const,
		onPreview: canViewImages ? handlePreview : undefined,
		showUploadList: {
			showPreviewIcon: canViewImages,
			showRemoveIcon: true,
		},
	};

	const propsUploadPrimary = {
		name: 'file',
		accept: '.jpg,.jpeg,.png,.webp',
		maxCount: 1,
		fileList: primaryFileList,
		beforeUpload: () => false,
		listType: 'picture-card' as const,
		onPreview: canViewImages ? handlePreview : undefined,
		showUploadList: {
			showPreviewIcon: canViewImages,
			showRemoveIcon: true,
		},
	};

	return (
		<div className={styles['side-drawer-body']}>
			<Form
				form={form}
				name="groceryForm"
				initialValues={defaultAddValues}
				onFinish={onFinish}
				autoComplete="off"
				layout="vertical"
				labelAlign="left"
				className={styles['side-drawer-form']}
			>
				<div className={styles['side-drawer-form__inputs']}>
					{addGroceryFields.map((singleField: any, index: number) => {
						if (
							(singleField.type === 'primaryImage' ||
								singleField.type === 'secondaryImage') &&
							!canViewImages
						) {
							return null;
						}

						return (
							<React.Fragment key={index}>
								{singleField.type === 'text' && (
									<div
										className={
											shouldDisableNonImageFields
												? styles['side-drawer-form__disabled-field']
												: ''
										}
									>
										<AutoCompleteField
											form={form}
											singleUserInput={singleField}
											options={
												optionsState[
													singleField?.name as keyof typeof optionsState
												]
											}
											onSearch={handleSearch}
											disabled={shouldDisableNonImageFields}
										/>
									</div>
								)}

								{singleField.type === 'number' && (
									<div
										className={
											shouldDisableNonImageFields
												? styles['side-drawer-form__disabled-field']
												: ''
										}
									>
										<InputWithLabelAndSvg
											singleUserInput={singleField}
											disabled={shouldDisableNonImageFields}
										/>
									</div>
								)}

								{singleField.type === 'dropdown' && (
									<Form.Item
										key={singleField.id}
										name={singleField.name}
										label={singleField.title}
										rules={singleField.rules}
										className={`${styles['side-drawer-form__medicine--select']} ${
											shouldDisableNonImageFields
												? styles['side-drawer-form__medicine--select-disabled']
												: ''
										}`}
									>
										<Select
											placeholder={
												singleField.id === 'subCategoryId' && !selectedCategoryId
													? 'Select category first'
													: singleField.placeHolder
											}
											options={getDropdownOptions(
												singleField.id,
												singleField.roleOptions,
											)}
											showSearch
											optionFilterProp="label"
											style={{ height: '42px' }}
											disabled={shouldDisableNonImageFields}
											onChange={(val: string) => {
												if (singleField.id === 'categoryId') {
													setSelectedCategoryId(val || '');
													if (!isApplyingClipboardRef.current) {
														form.setFieldsValue({ subCategoryId: undefined });
													}
												}
											}}
										/>
									</Form.Item>
								)}

								{singleField.type === 'primaryImage' && canManageImages && (
									<Form.Item label="Primary Image" name={singleField.name}>
										<Upload
											{...propsUploadPrimary}
											onChange={(info) => {
												handleChange(info, 'primary');
												setPrimaryFileList(info.fileList);
											}}
										>
											{primaryFileList.length >= 1 ? null : uploadButton}
										</Upload>
									</Form.Item>
								)}

								{singleField.type === 'secondaryImage' && canManageImages && (
									<Form.Item label="Secondary Images" name={singleField.name}>
										<Upload
											{...propsUpload}
											onChange={(info) => {
												handleChange(info, 'secondary');
												setFileList(info.fileList);
											}}
										>
											{fileList.length >= 3 ? null : uploadButton}
										</Upload>
									</Form.Item>
								)}
							</React.Fragment>
						);
					})}
				</div>

				{previewImage && (
					<Image
						wrapperStyle={{ display: 'none' }}
						preview={{
							visible: previewOpen,
							onVisibleChange: (visible) => setPreviewOpen(visible),
							afterOpenChange: (visible) => !visible && setPreviewImage(''),
						}}
						src={previewImage}
					/>
				)}

				<div className={styles['side-drawer-form__buttons']}>
					{!isEditMode && isGroceryAddPermission && (
						<Button
							type="default"
							className={styles['side-drawer-form__buttons--cancel']}
							onClick={handlePasteGroceryFields}
							disabled={loading || shouldDisableNonImageFields}
						>
							Paste
						</Button>
					)}
					<Button
						htmlType="submit"
						disabled={loading || !canSubmit}
						className={styles['side-drawer-form__buttons--edit']}
						loading={loading}
					>
						{isEditMode ? 'Update' : 'Save'}
					</Button>
					{isEditMode && (isGroceryEditPermission || isImageEditPermission) && (
						<Button
							type="default"
							className={styles['side-drawer-form__buttons--cancel']}
							onClick={handleCopyGroceryFields}
							disabled={loading}
						>
							Copy
						</Button>
					)}
					<Button
						htmlType="reset"
						className={styles['side-drawer-form__buttons--cancel']}
						onClick={handleOnClick}
					>
						Cancel
					</Button>
				</div>
			</Form>
		</div>
	);
};

export default AddEditGroceryBody;
